import { Injectable, SecurityContext, inject } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

export namespace NodeContentTypes {
  export type Empty = { type: "empty" };
  export type Link = { type: "link"; href: string; name: string; target: string };
  export type Html = { type: "html"; html: SafeHtml | null };
}
export type NodeContentTypes = NodeContentTypes.Empty | NodeContentTypes.Link | NodeContentTypes.Html;

@Injectable({ providedIn: "root" })
export class ContentCheckerService {
  private readonly sanitizer = inject(DomSanitizer);

  private parseContent(value: string): HTMLDivElement | undefined {
    const safeHtml = this.sanitizer.sanitize(SecurityContext.HTML, value);
    if (!safeHtml) return;
    const sanitizedContent = safeHtml.toString().trim();
    if (!sanitizedContent.length) return;
    const div = document.createElement("div");
    div.innerHTML = sanitizedContent;
    return div;
  }

  getContentLink(value: string): HTMLAnchorElement | null {
    return this.parseContent(value)?.querySelector<HTMLAnchorElement>("a[href]") ?? null;
  }

  getContentType(value: string | undefined | null): NodeContentTypes {
    if (value == null || !value.length) return { type: "empty" };

    const parsedContent = this.parseContent(value);
    if (!parsedContent) return { type: "empty" };

    const firstLink = parsedContent.querySelector<HTMLAnchorElement>("a[href]") ?? null;

    if (firstLink) {
      firstLink?.remove();
    }
    if (!parsedContent.innerText.trim().length) {
      // No other content just the link
      const link = firstLink && parseAnchorLink(firstLink);
      return link ?? { type: "empty" };
    } else {
      const html = value ? this.sanitizer.bypassSecurityTrustHtml(value) : null;
      return { type: "html", html };
    }
  }
}

export function parseAnchorLink(anchor: HTMLAnchorElement): NodeContentTypes.Link | null {
  const href = anchor.href?.trim();
  if (!href) return null;
  return { type: "link", href, name: anchor.title || "Site", target: anchor.target ?? "" };
}
